// Maps the claim service's billing reads onto what the outpatient summary displays.
//
// Deliberately does no money arithmetic. The clamp that keeps Cover + Excess == Total Billing
// (and Cover non-negative) lives in the claim service's shared NetPaidTotals rule, so every
// endpoint reporting totals agrees by construction; re-deriving it here would fork that rule.
// See CONTEXT.md — "Net Summary Source" and "Unknown vs Zero".

/** One saved worksheet row from GET /api/Claims/Provider/BillingWorksheet/{claimNumber}. */
export interface BillingWorksheetLine {
  idBenefit: number
  itemName?: string | null
  subBenefitCode?: string | null
  claimAmount: number
  exclusionAmountTo: number
  /** GROSS: the line's own co-share and prorate are already inside. */
  coverAmount: number
  excessAmount: number
  coShareFrom: number
  coShareTo: number
  /** Nominal line co-share (Pass 1) — informational; never subtract it again. */
  coShareAmount: number
  remark?: string | null
  isShow: boolean
}

/** Same vocabulary as the save/validate rollup responses: totalPaid/totalExcess are GROSS. */
export interface BillingWorksheetTotals {
  totalClaim: number
  totalPaid: number
  totalExcess: number
  totalCoverNet: number
  totalExcessNet: number
}

/** Persisted claim-level plan adjustment components. */
export interface BillingWorksheetApplied {
  deductionApplied: number
  coShareApplied: number
}

export interface BillingWorksheetData {
  claimNumber: string
  applied: BillingWorksheetApplied
  totals: BillingWorksheetTotals
  lines: BillingWorksheetLine[]
}

/** The subset of the Check Billing response carrying the same figures under its own names. */
export interface BillingValidationResult {
  totalClaim?: number
  totalPaid?: number
  totalExcess?: number
  totalCoverNet?: number
  totalExcessNet?: number
  deductibleApplied?: number
  coShareApplied?: number
  deductibleRequiresReview?: boolean
}

export interface BillingSummary {
  totalBilling: number
  /** Σ gross line covers — the first rung of the ladder, and what the Cover column adds up to. */
  grossCover: number
  totalCoverNet: number
  totalExcessNet: number
  deductionApplied: number
  coShareApplied: number
  /** False when there is no plan adjustment to explain, which hides the ladder row entirely. */
  hasPlanAdjustment: boolean
  requiresReview: boolean
}

/** Absent, non-numeric and NaN all mean "unknown"; 0 is a real zero and survives. */
const figure = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

const toSummary = (
  totalBilling: number | null,
  grossCover: number | null,
  totalCoverNet: number | null,
  totalExcessNet: number | null,
  deductionApplied: number | null,
  coShareApplied: number | null,
  requiresReview: boolean
): BillingSummary | null => {
  if (
    totalBilling === null ||
    grossCover === null ||
    totalCoverNet === null ||
    totalExcessNet === null ||
    deductionApplied === null ||
    coShareApplied === null
  ) {
    // Any missing figure makes the summary unknown rather than partially rendered: a net total
    // shown without its adjustments reads as gross, which understates member liability.
    return null
  }

  return {
    totalBilling,
    grossCover,
    totalCoverNet,
    totalExcessNet,
    deductionApplied,
    coShareApplied,
    hasPlanAdjustment: deductionApplied !== 0 || coShareApplied !== 0,
    requiresReview,
  }
}

/** Initial load / post-save re-read. requiresReview is always false: the flag is not persisted. */
export const summaryFromWorksheet = (data?: BillingWorksheetData | null): BillingSummary | null => {
  if (!data) return null

  return toSummary(
    figure(data.totals?.totalClaim),
    figure(data.totals?.totalPaid),
    figure(data.totals?.totalCoverNet),
    figure(data.totals?.totalExcessNet),
    figure(data.applied?.deductionApplied),
    figure(data.applied?.coShareApplied),
    false
  )
}

/** After Check Billing, where a fresh rollup ran and the review flag is meaningful. */
export const summaryFromValidation = (
  result?: BillingValidationResult | null
): BillingSummary | null => {
  if (!result) return null

  return toSummary(
    figure(result.totalClaim),
    figure(result.totalPaid),
    figure(result.totalCoverNet),
    figure(result.totalExcessNet),
    figure(result.deductibleApplied),
    figure(result.coShareApplied),
    result.deductibleRequiresReview === true
  )
}

/** The per-sub-benefit form state, keyed the way the billing table is keyed (sub-benefit name). */
export interface BillingFormSeed {
  costs: Record<string, number>
  excess: Record<string, number>
  newTotal: Record<string, number>
  newExcess: Record<string, number>
  newRemark: Record<string, string>
  coShare: Record<string, number>
}

/**
 * Seeds the billing inputs from the saved lines.
 *
 * `isShow` is not used to filter here: the table renders its rows from the claim's benefit
 * structure, not from the lines, so a hidden line only ever seeds a zero into a row that is
 * rendered anyway. Totals never come from these values — they come from `totals`.
 */
export const seedFromLines = (lines?: BillingWorksheetLine[] | null): BillingFormSeed => {
  const seed: BillingFormSeed = {
    costs: {},
    excess: {},
    newTotal: {},
    newExcess: {},
    newRemark: {},
    coShare: {},
  }

  ;(lines ?? []).forEach((line) => {
    const name = line?.itemName
    if (!name) return

    seed.costs[name] = Number(line.claimAmount) || 0
    seed.excess[name] = Number(line.exclusionAmountTo) || 0
    seed.newTotal[name] = Number(line.coverAmount) || 0
    seed.newExcess[name] = Number(line.excessAmount) || 0
    seed.newRemark[name] = line.remark || ''
    seed.coShare[name] = Number(line.coShareTo) || 0
  })

  return seed
}

/**
 * Which billing rows to expand on load: those whose saved claim amount is above zero, so the
 * provider can review every billable item without opening each row by hand. Rows with no amount
 * stay collapsed.
 *
 * `rowNames` is the rendered row order (sub-benefit names), since rows come from the claim's benefit
 * structure while amounts come from the saved lines — the two are matched by name.
 */
export const billableRowIndexes = (
  rowNames: (string | null | undefined)[],
  lines?: BillingWorksheetLine[] | null
): number[] => {
  const billable = new Set(
    (lines ?? [])
      .filter((line) => (Number(line?.claimAmount) || 0) > 0)
      .map((line) => line?.itemName)
      .filter((name): name is string => !!name)
  )

  return rowNames.reduce<number[]>((indexes, name, index) => {
    if (name && billable.has(name)) {
      indexes.push(index)
    }
    return indexes
  }, [])
}
